import { IColumn } from '@fluentui/react';
import { IPText } from 'baseComponents';
import { IPFontIcon } from 'baseComponents/FontIcon/IPFontIcon';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTooltipHost } from 'baseComponents/TooltipHost/IPTooltipHost';
import { IPSelectionMode } from 'baseComponents/Utils/Selection';
import BaseTableList, { IBaseTableList } from 'components/TableList/BaseTableList';
import { globalTextStylesError } from 'globalStyles';
import TaskType from 'models/tasks/taskType';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sortOnString } from 'utils/sorting';

export interface IFormsListProps {
  forms: TaskType[];
  selectedFormIds: number[];
  isLoading: boolean;
  loadError: boolean;
  onSelectForms: (forms: TaskType[]) => void;
}

const FormsList = (props: IFormsListProps) => {
  const { t } = useTranslation(['fieldsLibrary', 'translation']);

  const { forms, selectedFormIds, isLoading, loadError, onSelectForms } = props;
  const listRef = useRef<IBaseTableList<TaskType> | null>(null);

  const getColumnsList = (): IColumn[] => [
    {
      key: 'managed',
      name: '',
      minWidth: 16,
      maxWidth: 16,
      isResizable: false,
    },
    {
      key: 'name',
      name: t('translation:General.EntityType.Name'),
      minWidth: 150,
      isMultiline: true,
      isRowHeader: true,
      isResizable: true,
      isSorted: true,
      isSortedDescending: false,
    },
  ];

  const [columns, setColumns] = useState<IColumn[]>(getColumnsList());

  useEffect(() => {
    if (selectedFormIds.length !== 1) return;
    const index = forms.findIndex((form) => form.taskTypeId === selectedFormIds[0]);
    if (index < 0) return;

    //the rail follows the form the tab holds, so that form is the selection, not one more row
    //next to the one the reader came from
    listRef.current?.scroll(index, true, forms, true);
  }, [forms, selectedFormIds]);

  const onRenderColumn = (form: TaskType | undefined, column: IColumn): JSX.Element | null => {
    if (!form || !column) return null;

    switch (column.key) {
      case 'name':
        return <IPText>{form.name}</IPText>;
      case 'managed':
        if (!form.systemTaskType) return null;

        return (
          <IPTooltipHost content={t('forms:Errors.SystemTaskTypeDelete')}>
            <IPText>
              <IPFontIcon iconName="Lock" />
            </IPText>
          </IPTooltipHost>
        );
      default:
        return null;
    }
  };

  if (loadError && !isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center" styles={{ root: { paddingTop: 24 } }}>
        <IPText styles={globalTextStylesError}>{t('fieldsLibrary:Forms.LoadError')}</IPText>
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill>
      <BaseTableList
        ref={listRef}
        items={forms}
        columns={columns}
        selectionMode={IPSelectionMode.multiple}
        isLoading={isLoading}
        shimmerLines={8}
        noTopPadding
        getKey={(form) => String(form.taskTypeId)}
        isSortEnabled={(column) => column.key === 'name'}
        compare={(formA, formB, columnKey) => (columnKey === 'name' ? sortOnString(formA.name, formB.name) : 0)}
        setColumns={setColumns}
        onRenderCol={onRenderColumn}
        onSelect={onSelectForms}
      />
    </IPStack>
  );
};

export default FormsList;
